import React from 'react'
import { useSelector } from 'react-redux'
import { Grid, Typography } from '@mui/material'
import "./FrontDesk.scss";

const calculateGSTAmount = (tripDetails) => {
  if (tripDetails) {
    const gstBreakdown = tripDetails?.breakdown?.find(item => item.chargeType === 'gst');
    return gstBreakdown?.addedCharge || 0;
  }
  return (tripDetails?.cgstAmount || 0) + (tripDetails?.sgstAmount || 0);
};

function formatPrice(value) {
  if (isNaN(value)) return value;
  return Number(value).toLocaleString('en-IN'); // 'en-IN' → Indian format (e.g., 92,495)
}

const getTripCharges = (tripDetails) => {
  if (!tripDetails) return 0;
  const excludedTypes = ['tollCharges', 'parkingCharge', 'discountAmount', 'gst', 'tip', 'pointsRedeem', 'garageCharge'];
  const baseAmount = tripDetails.baseRate || 0;
  const rideFee = tripDetails?.baseFareDisplay  || tripDetails?.subtotal - (tripDetails?.baseRate + tripDetails?.surCharge + tripDetails?.platformCharge) || 0;
  const surcharge = tripDetails.surCharge || 0;
  const platformCharge = tripDetails.platformCharge || 0;
  
  const additionalCharges = tripDetails?.breakdown
    ?.filter(item => !excludedTypes.includes(item.chargeType))
    ?.reduce((sum, item) => sum + (item.addedCharge || 0), 0) || 0;

  return rideFee + baseAmount + surcharge + platformCharge + additionalCharges;
};

const getBreakdownAmount = (tripDetails, chargeType) => {
  return tripDetails?.breakdown?.find(item => item.chargeType === chargeType)?.addedCharge || 0;
};

function FareBreakUp(props) {

  const tripDetails = useSelector((state) => state?.userRateCalculation?.userRateCalculation?.data?.rate);

    const renderChargeSection = (label, amount, subBreakup = null, isNegative = false) => {
    if (!amount || amount <= 0) return null;

    return (
      <Grid item container
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        className='marginbottom20'>
        <Grid item>
          <Typography style={{fontSize: '1.25rem', fontWeight: 500}}>{label}</Typography>
          {subBreakup}
        </Grid>
        <Grid item>
          <Typography className={`font12 ${isNegative ? 'colorRed' : ''}`}>
            {isNegative ? `-₹${formatPrice(Math.abs(amount))}` : `₹${formatPrice(amount)}`}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  const renderTripChargesBreakup = () => {
    const breakupItems = [
      { label: 'Base Rate', value: tripDetails?.baseRate },
      { 
        label: (props?.type == 1 || props?.type == 4) ? 'Total Base Fare' : 'Package Cost',
        value: tripDetails?.subtotal - (tripDetails?.baseRate + tripDetails?.surCharge + tripDetails?.platformCharge + tripDetails?.breakdown?.find(item => item.chargeType === 'garageCharge')?.addedCharge || 0),
        ratePer: (props?.type == 1 || props?.type == 4) ? {lable: 'Rate per KM', value: tripDetails?.baseRatePerKM} : null
      },
      { label: 'Surcharge', value: tripDetails?.surCharge },
      { label: 'Platform Charge', value: tripDetails?.platformCharge },
      { label: 'Peak Hour Charges', value: getBreakdownAmount(tripDetails, 'peakHour') },
      { label: 'Waiting Charges', value: getBreakdownAmount(tripDetails, 'waitingTimeCharge') },
      { label: 'Cancellation Charges', value: getBreakdownAmount(tripDetails, 'cancellationCharge') },
      { label: 'Extra KM', value: getBreakdownAmount(tripDetails, 'extraKm') },
      { label: 'Extra Hours', value: getBreakdownAmount(tripDetails, 'extraHrs') },
      { label: 'Driver Allowance', value: getBreakdownAmount(tripDetails, 'driverAllowance') },
      { label: 'Night Allowance', value: getBreakdownAmount(tripDetails, 'nightAllowance') },
      { label: 'Aditional Charges', value: getBreakdownAmount(tripDetails, 'additionalCharges') },
      { label: 'Placard Charges', value: getBreakdownAmount(tripDetails, 'placard') },
    ];

    return (
      <>
        {breakupItems
          .filter(item => item.value > 0)
          .map((item, index) => (
            <Typography key={index} className='colorGrey font24 fontSemiBold'>
              {`${item.label}: ₹${formatPrice(item.value)} ${item.ratePer ? `@(${item.ratePer.lable}: ₹${formatPrice(item.ratePer.value)})` : ''}`}
            </Typography>
          ))}
      </>
    );
  };


  return (
    <div>
    <Grid container
      direction='column'
      justifyContent='center'
      alignItems='start'
    >

      {/* Bill Details Section */}
      <Grid container item
        direction='column'
        className='marginTop25' md={12}
      >
        <Grid container item
          direction='column'
          justifyContent='space-between'
          alignItems='center'
          className='billdetails' md={12}>

            {/* Main Charges Sections */}
          {renderChargeSection('Trip Charges', getTripCharges(tripDetails), renderTripChargesBreakup())}

          {/* Points Redeemed Section */}
          {renderChargeSection('Points Redeemed', getBreakdownAmount(tripDetails, 'pointsRedeem'), null, true)}
          
          {renderChargeSection('Tolls & Parking', 
            (getBreakdownAmount(tripDetails, 'tollCharges') + getBreakdownAmount(tripDetails, 'parkingCharge')))}
          
          {renderChargeSection('Discount', 
            Math.abs(getBreakdownAmount(tripDetails, 'discountAmount')), null, true)}
          
          {renderChargeSection('GST', calculateGSTAmount(tripDetails),
            tripDetails?.breakdown?.find(item => item.chargeType === 'gst')?.meta?.breakup?.map((tax, idx) => (
              <Typography key={idx} className='colorGrey font12'>
                {`${tax.name.toUpperCase()}: ${tax.unitValue}% (₹${tax.totalCharge})`}
              </Typography>
            ))
          )}
          
          {renderChargeSection('Tip', getBreakdownAmount(tripDetails, 'tip'))}

          {tripDetails?.breakdown?.find(item => item.chargeType === 'garageCharge')?.addedCharge > 0 && (
            <Grid item container
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              className='marginbottom20'>
              <Grid item>
                <Typography className='font20'>Garage To Garage Charge</Typography>
                <>
                  <Typography className='colorGrey font12'>{`Garage To Pickup: ₹${tripDetails?.breakdown?.find(item => item.chargeType === 'garageCharge')?.meta?.breakup[0]?.rate || 0}`}</Typography>
                  <Typography className='colorGrey font12'>{`Drop To Garage: ₹${tripDetails?.breakdown?.find(item => item.chargeType === 'garageCharge')?.meta?.breakup[1]?.rate || 0}`}</Typography>
                </>
              </Grid>
              <Grid item>
                <Typography className='font20'>{`₹${tripDetails?.breakdown?.find(item => item.chargeType === 'garageCharge').addedCharge}`}</Typography>
              </Grid>
            </Grid>
          )}

          {/* Total Amount */}
          <Grid item container
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            className='marginbottom20 solidTopLine paddingTop20'>
            <Grid item>
              <Typography className='font20 fontMedium'>Total</Typography>
              <Typography className='colorGrey font16'>Final Amount</Typography>
            </Grid>
            <Grid item>
              <Typography className='font20 fontMedium'>{`₹${tripDetails?.totalAmount || tripDetails?.totalAmount || 0}`}</Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>

    <Grid container className='justifyBetween p10 mtb10'>
        <Grid item xs={12}>
          <Grid item container>
            <Grid item className='noteGrid'>
              <Typography variant='body3' className='colorPrimary mr15'>Note - </Typography>
            </Grid>
            <Grid item xs flexDirection='column'>
              <Typography variant='body3' className='colorPrimary'>*Exclusive of Toll & Parking charges</Typography><br/>
              <Typography variant='body3' className='colorPrimary'>*The actual bill will be generated based on the actual usage</Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  )
}

export default FareBreakUp